import { useRef, useState } from "react";

/**
 * Wraps an async action in an in-flight guard.
 *
 * Restores the per-row busy behavior the pre-migration DocumentsPortalLayout had
 * on its Download buttons: while a download is running the control is disabled
 * and shows a wait cursor, so a double-click cannot start a second request.
 *
 * The ref guard (not just the state flag) is what actually blocks a second call
 * inside the same tick, before React has re-rendered with isBusy === true.
 */
export function useBusyAction<TArgs extends any[]>(
  action: (...args: TArgs) => void | Promise<void>,
) {
  const [isBusy, setIsBusy] = useState(false);
  const inFlight = useRef(false);

  const run = async (...args: TArgs) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setIsBusy(true);
    try {
      await action(...args);
    } finally {
      inFlight.current = false;
      setIsBusy(false);
    }
  };

  return { isBusy, run };
}
